import { createFileRoute, isRedirect, notFound, redirect } from '@tanstack/react-router';
import { ReviewForm, isStep } from '../components/form/ReviewForm.js';
import { ReviewsJetBadge } from '../components/form/ReviewsJetBadge.js';
import {
    FormNotAvailable,
    fetchContact,
    fetchReviewForm,
    showsBranding,
} from '../lib/review-form.js';
import { NO_CONFETTI_SITES } from '../lib/celebrate.js';
import { backdropFor } from '../lib/contrast.js';
import { seo } from '../lib/seo.js';

/**
 * `/f/:slug` — the page a business sends its customers to.
 *
 * ---------------------------------------------------------------------------
 * The one page here that is not marketing
 * ---------------------------------------------------------------------------
 * Everything else on this site is addressed to a business considering
 * ReviewsJet. This is addressed to *their* customer, who has followed a link
 * from an email or a receipt, has never heard of us, and wants to answer two
 * questions and leave. So it renders without the site's header, footer or
 * navigation — there is nowhere on this site they were trying to go.
 *
 * `formUrl` in the shared package builds the links the dashboard hands out, and
 * this is the route they point at. The path is not free to change.
 *
 * ---------------------------------------------------------------------------
 * Not indexed
 * ---------------------------------------------------------------------------
 * `noIndex`, because a search result for somebody else's review form is a
 * result nobody wants: not the searcher, who cannot use it, and not the
 * business, whose form would collect reviews from strangers who have never
 * bought anything. The title and card metadata are still built properly — they
 * are what appears when the link is pasted into a chat, which is exactly how
 * these get shared.
 *
 * ---------------------------------------------------------------------------
 * Query parameters
 * ---------------------------------------------------------------------------
 *   ?step=review    open partway in, as the legacy links did
 *   ?email=<id>     the review request this link was sent with. The form
 *                   greets the customer and skips asking for an address, and
 *                   the submission carries the id through — see `requestId`
 *                   on `ReviewForm` — so the review it produces is marked
 *                   verified and linked back to the request that sent them.
 *   ?product=<id>   the store's product this review is about. Set by the
 *                   product widget's "write a review" button and by nothing
 *                   else, so a review written from under a product is stored
 *                   as a review of that product rather than of the business.
 *
 * The legacy page also read `?campaign=`, and the rebuilt submit endpoint has
 * nowhere to put it, so it is not sent — a field the server drops is worse than
 * no field, because it looks like attribution is being recorded when it is not.
 * Restore it here and on the endpoint together.
 */
export const Route = createFileRoute('/f/$slug')({
  validateSearch: (search: Record<string, unknown>) => ({
    step: isStep(search.step) ? search.step : undefined,
    email: typeof search.email === 'string' ? search.email : undefined,
    /*
      The store's product id, put there by the product widget's "write a
      review" button and by nothing else. A review submitted through this link
      is stored as a review of that product; every other way into this form
      leaves it absent and produces a review of the business.
    */
    product: typeof search.product === 'string' ? search.product : undefined,
  }),

  loaderDeps: ({ search }) => ({ email: search.email, step: search.step }),

  loader: async ({ params, deps }) => {
    try {
      /*
        Both at once. The contact lookup is independent of the form, and this
        page is frequently opened on a phone from an email — one round trip
        instead of two is the difference a reviewer actually feels.
      */
      const [data, contact] = await Promise.all([
        fetchReviewForm(params.slug),
        deps.email ? fetchContact(deps.email) : Promise.resolve(null),
      ]);

      /*
        One site to send them to, and the form set to send them there: there
        is nothing left for this page to ask, so it forwards — every "leave a
        review" button, from the request email to a QR code, ends up on the
        site in one step. The contact lookup above has already run, so a
        request is still recorded as opened.
      */
      if (data.form.directTo && !deps.step) {
        throw redirect({ href: data.form.directTo });
      }

      return { ...data, contact };
    } catch (error) {
      // A slug nobody owns is a 404, not a 500. Anything else is a real
      // failure and should surface as one rather than as "form not found".
      if (isRedirect(error)) throw error;
      if (error instanceof FormNotAvailable) throw notFound();
      throw error;
    }
  },

  head: ({ loaderData }) => {
    const business = loaderData?.business?.name ?? '';
    const title = business ? `Rate your experience · ${business}` : 'Leave a review';

    return seo({
      title,
      description: business
        ? `Share your experience with ${business}. It takes less than a minute.`
        : 'Share your experience. It takes less than a minute.',
      path: `/f/${loaderData?.form.slug ?? ''}`,
      noIndex: true,
      // No canonical either. It follows `noIndex` — a canonical URL is a
      // statement to a crawler about a page we have just asked it not to index.
      canonical: false,
    });
  },

  component: HostedForm,

  /*
    A dead review link, answered plainly.

    The site's own 404 offers a list of widget layouts and review sources,
    which is the right page for somebody who mistyped a marketing URL and
    exactly the wrong one here: this visitor was asked by a business they know
    to leave a review, the link did not work, and they have no interest in what
    we sell. Telling them the truth in one sentence — and that it is not their
    fault — is the whole job.

    No link back to this site either. There is nothing on it for them.
  */
  notFoundComponent: FormNotFound,
});

function FormNotFound() {
  return (
    <main
      id="main"
      className="flex min-h-screen w-full items-center justify-center bg-ink-25 p-5"
    >
      <div className="w-full max-w-[500px] rounded-[10px] border border-ink-150 bg-white p-6 text-center shadow-panel">
        <h1 className="text-[25px] leading-[35px] font-bold tracking-[-1px] text-[#55595f]">
          This review form isn’t available
        </h1>
        <p className="mt-2.5 text-caption leading-relaxed text-[#55595f]">
          The link may have expired, or the business may have closed the form.
          Nothing you did caused this — if you were asked to leave a review, it
          is worth letting them know the link no longer works.
        </p>
      </div>
    </main>
  );
}

function HostedForm() {
  const { form, contact, plan, business } = Route.useLoaderData();
  const { step, product, email } = Route.useSearch();
  const params = Route.useParams();

  // The page is a tint of the form's own colour; see `backdropFor`.
  const backdrop = backdropFor(form.appearance.primaryColor);

  return (
    <main
      id="main"
      // On a phone the form is the screen: no margin, no card edge, the
      // colour reaching the top and bottom of the display.
      className="flex min-h-screen w-full items-center justify-center p-5 max-sm:items-stretch max-sm:p-0"
      style={{
        backgroundColor: backdrop,
      }}
    >
      <div className="w-full max-w-[500px] max-sm:flex max-sm:min-h-[100dvh] max-sm:flex-col">
        <div className="rounded-[10px] border border-ink-150 bg-white p-6 shadow-panel max-sm:flex max-sm:flex-1 max-sm:flex-col max-sm:rounded-none max-sm:border-0 max-sm:shadow-none">
          <ReviewForm
            form={form}
            slug={params.slug}
            initialStep={step}
            contact={contact}
            requestId={email}
            productId={product}
            celebrate={
              business && NO_CONFETTI_SITES.includes(business.siteId) ? null : undefined
            }
          />
        </div>

        {/*
          Free plans only, and the same badge the free widgets draw. Removing
          it is what a paid plan buys, and this page — somebody else's customer
          looking at somebody else's brand — is the most visible place we could
          fail to honour that.
        */}
        {showsBranding(plan) && <ReviewsJetBadge widget="hosted-form" />}
      </div>
    </main>
  );
}

